// Geboortevenster · Bevestiging vóór het overnemen van een nieuwe geboortetijd (oud, nieuw, delta, waarschuwing handwerk).
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { ChangeDetectionStrategy, Component, computed, inject, model, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { hdBirthWindowTextKey } from './birth-window-format';

export interface HdBirthWindowConfirmTarget {
  readonly oldText: string;
  readonly newText: string;
  readonly deltaText: string;
  /** Chart nog niet opgeslagen: de nieuwe tijd komt enkel in de tab. */
  readonly unsaved: boolean;
  /** Chart van een moment (Just Now, Rave New Year): "tijdstip" i.p.v. "geboortetijd". */
  readonly moment?: boolean;
  readonly busy?: boolean;
}

const BW = 'humanDesign.chart.birthWindow';

/** Bevestiging vóór "Geboortetijd overnemen" (besluit 4): oude en nieuwe tijd, Δ en de waarschuwing over handwerk. */
@Component({
  selector: 'hd-birth-window-confirm-dialog',
  imports: [SharedModule],
  templateUrl: './hd-birth-window-confirm-dialog.component.html',
  styleUrl: './hd-birth-window-confirm-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdBirthWindowConfirmDialogComponent {
  readonly target = model<HdBirthWindowConfirmTarget | null>(null);
  readonly confirmed = output<void>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly text = computed(() => {
    // Zoals hd-moment-save-dialog: effectiveLang() leest zelf de laad-tick.
    const lang = this.contentLang.effectiveLang();
    const moment = this.target()?.moment ?? false;
    const t = (key: string) =>
      this.transloco.translate<string>(`${BW}.${hdBirthWindowTextKey(`commit.${key}`, moment)}`, undefined, lang);
    return {
      title: t('confirmTitle'),
      intro: t('confirmIntro'),
      old: t('confirmOld'),
      new: t('confirmNew'),
      confirm: t('confirmButton'),
      cancel: t('cancel'),
      unsavedHint: t('unsavedHint'),
    };
  });

  protected onOpenChange(open: boolean): void {
    if (!open) this.target.set(null);
  }

  protected confirm(): void {
    if (this.target()?.busy) return;
    this.confirmed.emit();
  }

  protected cancel(): void {
    this.target.set(null);
  }
}
